import { useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMultiStepModal } from '@frontend/payments-multistep-modal';
import {
  Button,
  ContentLoader,
  Heading,
  MessageTemplate,
  Modal,
  TagType,
  Text,
  useAlert,
} from '@frontend/design-system';
import { BULK_CREATE_STEPS } from '../bulk-create-steps-key-title';
import { styles } from './styles';

type SendSmsStepProps = {
  count: number;
  text: string;
  onSend: ({ useTTP, smsBody }: { useTTP?: boolean; smsBody?: string }) => void;
  isLoading: boolean;
  tags: TagType[];
};

export const SendSmsStep = ({ count, text, onSend, isLoading, tags }: SendSmsStepProps) => {
  const alert = useAlert();
  const { t } = useTranslation('payments');
  const { goToStep } = useMultiStepModal();

  const [smsBody, setSmsBody] = useState(text);

  const handleSend = (useTTP?: boolean) => {
    if (useTTP) {
      const missingRequiredTags = tags.filter((tag) => !tag.optional && !smsBody.includes(String(tag.key)));

      if (missingRequiredTags.length > 0) {
        alert.error(
          t('Please include all required fields in your message: {{fields}}', {
            fields: missingRequiredTags.map((tag) => `**${tag.label}**`).join(', '),
          })
        );
        return;
      }
    }

    onSend({ useTTP, smsBody });
  };

  return (
    <>
      <Modal.Body css={styles.modalBody()}>
        <Text>{t('It may take a few moments for requests to populate or update')}</Text>
        <div css={styles.sendSms.container}>
          <div css={styles.sendSms.containerHeaderWrapoper}>
            <div css={styles.sendSms.containerHeaderLeft}>
              <Text color='subdued'>{t('Sending to')}</Text>
              <Heading level={3}>{t('{{count}} Contacts', { count })}</Heading>
            </div>
            <div css={styles.sendSms.containerHeaderRight}>
              <Icon name='star' />
              <div>
                <Text weight='bold'>{t('Default Template')}</Text>
                <Text color='subdued'>{t('Bulk Payment Request')}</Text>
              </div>
            </div>
          </div>
          <ContentLoader show={isLoading} />
          <MessageTemplate
            draggableTags
            hasDynamicTags
            initialTemplate={text}
            isNewTag
            onChange={setSmsBody}
            tags={tags}
          >
            <MessageTemplate.Editor
              placeholder={t('Customize your message')}
              showEmojiPicker
              css={styles.sendSms.templateEditor}
            >
              <Button
                variant='tertiary'
                iconName='plus'
                onClick={() => goToStep(BULK_CREATE_STEPS.SELECT_TEMPLATE.id)}
                trackingId='bulk-collections-select-template-button'
              >
                {t('Select Template')}
              </Button>
            </MessageTemplate.Editor>
            <div css={styles.sendSms.tagListContainer}>
              <MessageTemplate.TagList showTagsOfType='REQUIRED' helperText={t('Required field *')} />
              <MessageTemplate.TagList
                showTagsOfType='OPTIONAL'
                helperText={t(
                  'You can drag or click the dynamic fields below to tailor your message to your recipient'
                )}
              />
            </div>
          </MessageTemplate>
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Send'),
          onClick: () => handleSend(true),
          trackingId: 'bulk-collections-send-sms-button',
          disabled: isLoading,
        }}
        secondary={{
          label: t('Finish without Sending'),
          onClick: () => handleSend(false),
          trackingId: 'bulk-collections-finish-without-sending-button',
          disabled: isLoading,
        }}
      />
    </>
  );
};
